import { Checkbox, FormControlLabel, FormHelperText } from '@mui/material';
import React from 'react';
import { V } from '../Layout';
import { FormProvider } from '../form/FormContext';
import FormModalInfoAlert from '../form/FormModalInfoAlert';
import FormWidget from '../form/FormWidget';
import Validator from '../form/Validator';
import I18n, { useI18n } from '../i18n/I18n';
import type { SavePreferenceConfig, SavePreferenceFormData } from '../types/savePreference';
import { ShareInfo, defaultOptions } from './shareInfo/ShareInfo';

const pinAsTabLabelSx = { mb: 0 };
const pinAsTabHelperTextSx = { mt: 0 };

export interface SavePreferenceFormProps {
    config: SavePreferenceConfig;
    formData: SavePreferenceFormData;
    onFormDataChange: (formData: SavePreferenceFormData) => void;
}

export default function SavePreferenceForm({ config, formData, onFormDataChange }: SavePreferenceFormProps) {
    const t = useI18n();
    return (
        <FormProvider
            state={formData}
            setState={onFormDataChange}
            validationRules={{ name: [Validator.RULES.isRequired] }}
            disableFunctionalStateUpdate
        >
            <V gap={2} sx={{ p: 2 }}>
                {config.notificationToken && (
                    <FormModalInfoAlert sx={{ p: 0, m: 0 }} descriptionToken={config.notificationToken} />
                )}
                <FormWidget
                    key="name"
                    name="name"
                    label={<I18n token="preference.form.name" />}
                    placeholder={t(config.namePlaceholder || 'preference.placeholder.name')}
                    fullWidth
                />
                <FormWidget
                    key="description"
                    name="description"
                    label={<I18n token="preference.form.description" />}
                    placeholder={t(config.descriptionPlaceholder || 'preference.placeholder.description')}
                    multiline
                    minRows={2}
                    fullWidth
                />
                {!config.hidePinAsTab && (
                    <V>
                        <FormControlLabel
                            sx={pinAsTabLabelSx}
                            control={
                                <Checkbox
                                    checked={Boolean(formData?.pinAsTab)}
                                    slotProps={{ input: { 'aria-describedby': 'pinAsTab-helper-text' } }}
                                    onChange={(_event, checked) => onFormDataChange({ ...formData, pinAsTab: checked })}
                                />
                            }
                            label={<I18n token="filter.savedFilter.pinAsTab" />}
                        />
                        <FormHelperText id="pinAsTab-helper-text" sx={pinAsTabHelperTextSx}>
                            <I18n token="filter.savedFilter.pinAsTab.help" />
                        </FormHelperText>
                    </V>
                )}
                {config.renderDataPreview && <V gap={1}>{config.renderDataPreview()}</V>}
                <ShareInfo
                    options={defaultOptions}
                    shared={formData?.shared}
                    onSharedChange={(shared) => onFormDataChange({ ...formData, shared })}
                />
            </V>
        </FormProvider>
    );
}
